/**
 * Australian portal dates: **DD/MM/YYYY** only (day first, four-digit year).
 *
 * Never parse user-entered `dd/mm/yyyy` with `new Date(string)` — ECMAScript treats many
 * slash forms as **US month/day/year**, which swaps meaning whenever day ≤ 12 (the classic
 * “MM/DD/YY” bug). Always use {@link ddMmYyyyToIsoDateValue}, {@link parseDdMmYyyyToDate},
 * and related helpers here so order stays unambiguous.
 */

/**
 * Strip non-digits, keep up to 8 digits, insert slashes as DD/MM/YYYY while typing or pasting.
 * A slash after the day appears only once a month digit follows (3+ digits); a slash after the
 * month appears only once a year digit follows (5+ digits). Trailing slashes are omitted so
 * backspacing removes segment separators cleanly (e.g. 02/0 → 02, not 02/).
 */
export function formatDdMmYyyyAsUserTypes(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8)
  const len = digits.length
  if (len === 0) return ''
  if (len <= 2) return digits
  if (len <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

export function ddMmYyyyToIsoDateValue(ddMmYyyy: string): string {
  const raw = ddMmYyyy.trim()
  if (!raw) return ''
  const parts = raw.split('/')
  if (parts.length !== 3) return ''
  const [ds, ms, ys] = parts
  if (!/^\d{1,2}$/.test(ds) || !/^\d{1,2}$/.test(ms) || !/^\d{4}$/.test(ys)) return ''
  const d = parseInt(ds, 10)
  const m = parseInt(ms, 10)
  const y = parseInt(ys, 10)
  if (m < 1 || m > 12 || d < 1 || d > 31) return ''
  const t = Date.UTC(y, m - 1, d)
  if (Number.isNaN(t)) return ''
  const dt = new Date(t)
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return ''
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

export function isoDateValueToDdMmYyyy(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return ''
  const year = parseInt(m[1], 10)
  const month = parseInt(m[2], 10)
  const day = parseInt(m[3], 10)
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return ''
  const t = Date.UTC(year, month - 1, day)
  if (Number.isNaN(t)) return ''
  const roundTrip = new Date(t).toISOString().slice(0, 10)
  if (roundTrip !== iso) return ''
  return `${m[3]}/${m[2]}/${m[1]}`
}

/** Local midnight for “today” (calendar max date in the user’s timezone). */
export function startOfTodayLocal(): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

/**
 * Parse a complete DD/MM/YYYY string to a local Date, or null if invalid / incomplete.
 * Reuses {@link ddMmYyyyToIsoDateValue} so impossible calendar dates are rejected.
 */
export function parseDdMmYyyyToDate(ddMmYyyy: string): Date | null {
  const iso = ddMmYyyyToIsoDateValue(ddMmYyyy.trim())
  if (!iso) return null
  const [y, m, day] = iso.split('-').map((s) => parseInt(s, 10))
  return new Date(y, m - 1, day)
}

/** Format a local calendar date as DD/MM/YYYY for the text field. */
export function formatDateToDdMmYyyy(date: Date): string {
  const d = date.getDate()
  const m = date.getMonth() + 1
  const y = date.getFullYear()
  return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${String(y).padStart(4, '0')}`
}

/** Earliest calendar day allowed in portal date fields (local), aligned with {@link DateInput} default min. */
export const PORTAL_DATE_MIN = new Date(1900, 0, 1)

function startOfLocalDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

/**
 * True when the value is a complete DD/MM/YYYY that exists on the calendar
 * (not e.g. 31/02/2003 or 11/42/4242).
 */
export function isValidDdMmYyyyCalendarDate(s: string): boolean {
  return ddMmYyyyToIsoDateValue(s.trim()) !== ''
}

/**
 * True when the value is a real calendar date and its local calendar day lies in
 * {@link minDay}–{@link maxDay} inclusive (e.g. 1900-01-01 through today for DOB/incident).
 */
export function isDdMmYyyyInLocalRange(s: string, minDay: Date, maxDay: Date): boolean {
  const parsed = parseDdMmYyyyToDate(s)
  if (!parsed) return false
  const t = startOfLocalDay(parsed)
  return t >= startOfLocalDay(minDay) && t <= startOfLocalDay(maxDay)
}
